import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, FileText, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabase';

interface Invoice {
  id: string;
  vendor: string;
  invoice_number: string;
  amount: number;
  currency: string;
  due_date: string;
  status: 'received' | 'under_review' | 'approved' | 'rejected' | 'paid';
}

interface StatusEvent {
  id: string;
  status: string;
  changed_by: string;
  changed_at: string;
  comment: string | null;
}

interface InvoiceDetailPanelProps {
  invoice: Invoice;
  onClose: () => void;
  onStatusChange: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
};

const ACTIONS_BY_STATUS: Record<string, { label: string; action: string; variant: 'default' | 'destructive' | 'outline' }[]> = {
  received: [{ label: 'Start Review', action: 'review', variant: 'default' }],
  under_review: [
    { label: 'Approve', action: 'approve', variant: 'default' },
    { label: 'Reject', action: 'reject', variant: 'destructive' },
  ],
  approved: [{ label: 'Mark Paid', action: 'mark_paid', variant: 'default' }],
  rejected: [{ label: 'Re-open Review', action: 'review', variant: 'outline' }],
  paid: [],
};

export function InvoiceDetailPanel({ invoice, onClose, onStatusChange }: InvoiceDetailPanelProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: history } = useQuery({
    queryKey: ['invoice-history', invoice.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_status_history')
        .select('id, status, changed_by, changed_at, comment')
        .eq('invoice_id', invoice.id)
        .order('changed_at', { ascending: false });
      if (error) throw error;
      return data as StatusEvent[];
    },
  });

  const invoiceAction = useMutation({
    mutationFn: async (action: string) => {
      setActionLoading(action);
      const { error } = await supabase.functions.invoke('invoice-action', {
        body: { invoice_id: invoice.id, action },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-history', invoice.id] });
      onStatusChange();
    },
    onSettled: () => setActionLoading(null),
  });

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const actions = ACTIONS_BY_STATUS[invoice.status] ?? [];

  return (
    <div className="w-[400px] shrink-0 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            Invoice Details
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Vendor</span>
              <p className="font-medium">{invoice.vendor}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Invoice #</span>
              <p className="font-medium">{invoice.invoice_number}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Amount</span>
              <p className="font-medium">{formatCurrency(invoice.amount, invoice.currency)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Due Date</span>
              <p className="font-medium">{new Date(invoice.due_date).toLocaleDateString()}</p>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Status</span>
              <p className="mt-1">
                <Badge variant="outline">{STATUS_LABELS[invoice.status] ?? invoice.status}</Badge>
              </p>
            </div>
          </div>

          {actions.length > 0 && (
            <>
              <Separator />
              <div className="flex flex-wrap gap-2">
                {actions.map((act) => (
                  <Button
                    key={act.action}
                    variant={act.variant}
                    size="sm"
                    disabled={actionLoading !== null}
                    onClick={() => invoiceAction.mutate(act.action)}
                  >
                    {actionLoading === act.action ? 'Processing...' : act.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Status History Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            Status History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history && history.length > 0 ? (
            <div className="space-y-3">
              {history.map((event) => (
                <div key={event.id} className="flex gap-3 text-sm">
                  <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <div>
                    <p className="font-medium">{STATUS_LABELS[event.status] ?? event.status}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(event.changed_at).toLocaleString()}
                    </p>
                    {event.comment && (
                      <p className="mt-1 text-muted-foreground">{event.comment}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No status changes recorded.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
